import { useSelector, useDispatch } from "react-redux";

import { Routing } from "./routes/Routing";
import DeprecationNotice from "./common/components/DeprecationNotice/DeprecationNotice";
import { DeprecationNoticeProvider } from "./common/components/DeprecationNotice/DeprecationNoticeContext";
import "./App.css";
import { useEffect, useRef } from "react";
import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addNotification } from "./redux/slices/notificationSlice";
import {
  startSignalRConnection,
  stopSignalRConnection,
} from "./operations/services/notificationSignalR";

/* ─── Notification chime via Web Audio API (~3.5 s, no file needed) ─── */
function playNotificationBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    // Helper: schedule one "ding" tone
    const ding = (startTime, freq, duration) => {

      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.25, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    // Two-tone chime repeated twice ≈ 3.5 s total
    ding(now, 880, 0.8); // A5
    ding(now + 0.5, 1174.66, 0.8); // D6
    ding(now + 1.6, 880, 0.8); // A5 (repeat)
    ding(now + 2.1, 1174.66, 1.2); // D6 long tail
  } catch (e) {
    // AudioContext not available — silently skip
  }
}

/* ─── Browser Notification popup ─── */
function showBrowserNotification(senderName, message) {
  if (Notification.permission === "granted") {
    new Notification(`New message from ${senderName || "someone"}`, {
      body: message?.slice(0, 100) || "",
      icon: "/favicon.ico",
    });
  }
}

function App() {
  const { isLoading } = useSelector((state) => state.login.login);
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state) => state.login?.login?.isAuthenticated,
  );
  const userId = useSelector(
    (state) =>
      state.login?.login?.data?.data?.[0]?.UserId ||
      state.login?.login?.data?.data?.[0]?.AdminID ||
      state.login?.login?.data?.data?.[0]?.AgentID,
  );

  useEffect(() => {
    console.log("[SignalR Trace] Auth Check:", {
      isAuthenticated,
      userId,
      hasNegotiateUrl: !!process.env.REACT_APP_SIGNALR_NEGOTIATE,
    });
  }, [isAuthenticated, userId]);

  // Track whether we've already started SignalR to avoid double-connect on re-renders
  const signalRStarted = useRef(false);

  // Request browser notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Start / stop SignalR based on auth state
  useEffect(() => {
    if (isAuthenticated && userId) {
      if (!signalRStarted.current) {
        console.log("[SignalR Trace] Starting connection for userId:", userId);

        // Get auth token from storage (check both localStorage and sessionStorage)
        const authToken =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!authToken) {
          console.warn(
            "[SignalR Trace] Auth token not found in storage. Deferring SignalR connection.",
          );
          return;
        }

        signalRStarted.current = true;

        startSignalRConnection(
          userId,
          (payload) => {
            // Build a notification object compatible with our panel
            const newNotif = {
              NotificationId: Date.now(), // temp ID for key
              SenderName: payload.senderName ?? "Someone",
              Message: payload.message,
              NotificationType: payload.type,
              NavigationPath: payload.navigationPath,
              IsRead: 0,
              CreatedAt: new Date().toISOString(),
            };

            dispatch(addNotification(newNotif));
            playNotificationBeep();
            showBrowserNotification(newNotif.SenderName, newNotif.Message);
          },
          authToken,
        );
      }
    } else {
      // Logged out or not authenticated
      if (signalRStarted.current) {
        stopSignalRConnection();
        signalRStarted.current = false;
      }
    }

    return () => {
      // Only fully tear down on unmount of App (page close / hard refresh)
      // Regular logout is handled by the else branch above
    };
  }, [isAuthenticated, userId, dispatch]);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: "no-store",
        });

        const data = await response.json();
        console.log("Fetched version: ", data);
        console.log("Current version:", process.env.REACT_APP_VERSION);
        console.log("Fetched version:", data.version);
        if (data.version !== process.env.REACT_APP_VERSION) {
          window.location.reload();
        }
      } catch (err) {}
    };

    const interval = setInterval(checkVersion, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <DeprecationNoticeProvider>
        <DeprecationNotice />
        <Routing />
      </DeprecationNoticeProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
}

export default App;
